import type {ChangeEvent} from 'react';
import {useId} from 'react';

import {media, spacing, translucify} from '@nfq/react-grid';
import styled from 'styled-components';

import type {WithChildren} from 'types/global';

interface ComponentProps {
    /**
     * Indicates whether the radio button is checked or not.
     */
    checked?: boolean;
    /**
     * The name attribute for the radio button.
     */
    name: string;
    /**
     * A callback function invoked when the radio button is changed.
     *
     * @param e The change event object.
     */
    onChange(e: ChangeEvent<HTMLInputElement>): void;
    required?: boolean;
    /**
     * Indicates whether the button should stretch.
     */
    stretch?: boolean;
    /**
     * The value associated with the radio button.
     * This value can be either a number or a string.
     */
    value: number | string;
}

/**
 * Renders a radio button component.
 * This component represents a single radio button.
 *
 * @param props          The component props.
 * @param props.checked  Indicates whether the radio button is checked or not.
 * @param props.children The content to be displayed next to the radio button.
 * @param props.name     The name attribute for the radio button.
 * @param props.onChange A callback function invoked when the radio button is changed.
 * @param props.value    The value associated with the radio button.
 * @param props.stretch  Indicates whether the button should stretch.
 * @param props.required Indicates whether the button is required.
 * @returns The JSX element representing the radio button.
 *
 * @example
 * ```tsx
 * const MyRadioButton = () => {
 *     const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
 *         console.log('Selected value:', e.target.value);
 *     };
 *
 *     return (
 *         <RadioButton
 *             checked={true}
 *             name="myRadioButton"
 *             value="option1"
 *             onChange={handleChange}
 *         >
 *             Option 1
 *         </RadioButton>
 *     );
 * };
 * ```
 */
export const RadioButton = ({
    checked,
    children,
    name,
    onChange,
    required,
    stretch,
    value
}: WithChildren<ComponentProps>) => {
    const id = useId();

    return (
        <Wrapper $stretch={stretch}>
            <Input
                checked={checked ?? false}
                id={id}
                name={name}
                required={required}
                type="radio"
                value={value}
                onChange={onChange}
            />
            <Label htmlFor={id}>{children}</Label>
        </Wrapper>
    );
};

interface WrapperProps {
    $stretch?: boolean;
}

const Wrapper = styled.span<WrapperProps>`
    display: inline-flex;
    ${({$stretch}) => ($stretch && 'flex-grow: 1;')}
    font: 1.6rem / 1.6 ${({theme}) => theme.fonts.Dinot};
    position: relative;
    width: ${({$stretch}) => ($stretch ? 'auto' : 'fit-content')};

    ${media('lg')} {
        font-size: 2rem;
    }
`;

const Input = styled.input`
    appearance: none;
    inset: 0;
    opacity: 0;
    outline: 0 none;
    position: absolute;
`;

const Label = styled.label`
    align-items: center;
    background: ${({theme}) => translucify(theme.colors.ultraPageForeground, 5)};
    border: 1px ${({theme}) => translucify(theme.colors.ultraPageForeground, 30)} solid;
    border-radius: 12px;
    color: ${({theme}) => theme.colors.ultraPageForeground};
    cursor: pointer;
    display: flex;
    justify-content: center;
    padding: ${spacing(3)} ${spacing(5)};
    position: relative;
    transition: border-color 0.2s ease-in-out;
    width: 100%;

    ${Input}:checked + & {
        border-color: ${({theme}) => theme.colors.ultraPageForeground};
    }

    &:hover {
        border-color: ${({theme}) => translucify(theme.colors.ultraPageForeground, 50)};
    }

    ${Input}:focus-visible + & {
        border-color: ${({theme}) => theme.colors.ultraFocusColor};
    }
`;